import { useActor } from "@xstate/react";
import React, { FunctionComponent, useEffect, useCallback } from "react";
import { BehaviorSubject } from "rxjs";
import { State } from "xstate";
import Alert from "@mui/material/Alert";
import { Content } from "@iventis/translations/content/typed-content";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Checkbox, Collapse, Divider, List, ListItem, ListItemText } from "@mui/material";
import { Node } from "@iventis/domain-model/model/node";
import { styled } from "@iventis/styles";
import { Body2 } from "@iventis/styles/src/components/texts";
import { canRead } from "@iventis/utilities";
import { CopyVersionsMachineEvents, IdAndParentId, NodeWithExpanded, COPY_MACHINE_ID } from "./copy-schedule-versions.machine";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CopyScheduleVersionComponent: FunctionComponent<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parentState: State<any, any, any, any, any>;
    childType: string;
    isCopyMachineValid$?: BehaviorSubject<boolean>;
}> = ({ parentState, childType, isCopyMachineValid$ }) => {
    const [state, send] = useActor(parentState.children[COPY_MACHINE_ID]);
    const translate = useIventisTranslate();
    useEffect(() => isCopyMachineValid$?.next(state.context.scheduleVersionIdsToCopy.length > 0));

    return (
        <>
            {state?.context?.scheduleVersionIdsToCopy?.length > 0 && (
                <Alert style={{ marginBottom: "5px" }} severity="info">
                    {translate(Content.schedule.copy_schedule_alert)}
                </Alert>
            )}

            <DropDownCheckBoxList
                piblings={state.context.piblingSchedules || []}
                send={(event: CopyVersionsMachineEvents) => send(event)}
                childIdsToCopy={state.context.scheduleVersionIdsToCopy || []}
                childType={childType}
                childNodeFilter={() => true}
            />
        </>
    );
};

/*
DropDownCheckBoxList:
Ability to provide a parent/header (Eg: Animals) and 1 level deep of children (Eg: Dog, Cat, Pig).
The Animal header is expandable and checkable. Checking the animal header selects all children.
All children are checkable. Selecting all children checks the parent/header.
Only children ids are included in the "selectedNodes" and SelectionChange" - Not headers.
*/
const DropDownCheckBoxList: FunctionComponent<{
    piblings: NodeWithExpanded[];
    send: (event: CopyVersionsMachineEvents) => void;
    childIdsToCopy: IdAndParentId[];
    childType: string;
    childNodeFilter?: (node: Node) => boolean;
}> = ({ piblings, send, childIdsToCopy, childType, childNodeFilter = () => true }) => {
    const isSelected = useCallback((id: string): boolean => childIdsToCopy.some((s) => s.id === id), [childIdsToCopy]);
    const translate = useIventisTranslate();
    return (
        <>
            {piblings.map((pibling, index) => (
                <StyledList key={pibling.id}>
                    {/* The parentNode list item */}
                    <ListItem
                        className={`parent-item ${index === piblings.length - 1 && "final-parent"} ${pibling.expandedInDialog && "expanded"}`}
                        key={`item-${pibling.id}`}
                        button
                        onClick={() => send({ type: "EXPAND_OR_COLLAPSE", payload: { id: pibling.id, parentId: null, name: pibling.name } })}
                    >
                        <FontAwesomeIcon className={`caret ${pibling.expandedInDialog && "expanded"}`} icon={pibling.expandedInDialog ? "caret-down" : "caret-right"} />
                        <ListItemText id={`checkbox-list-label-${pibling.id}`} primary={pibling.name} />
                        {pibling.expandedInDialog && pibling.childNodes.length > 0 && (
                            <Checkbox
                                onClick={(event) => {
                                    send({ type: "SELECT_OR_DESELECT_NODE", payload: { id: pibling.id, parentId: null, name: pibling.name } });
                                    event.stopPropagation();
                                }}
                                edge="start"
                                color="primary"
                                checked={isSelected(pibling.id)}
                                tabIndex={-1}
                                disableRipple
                                inputProps={{ "aria-labelledby": `checkbox-list-label-${pibling.id}` }}
                                disabled={!pibling.childNodes.some((child) => canRead(child.authorisation))}
                            />
                        )}
                    </ListItem>
                    {/* Collapse allows the list drop down to open and close */}
                    <Collapse in={pibling.expandedInDialog} timeout="auto" unmountOnExit>
                        <Divider />
                        <List className="child-item" component="div" disablePadding>
                            {pibling.childNodes.length === 0 && (
                                <Body2 style={{ margin: "5px", fontStyle: "italic" }}>{translate(Content.common.no_children_available, { childType })}</Body2>
                            )}
                            {/* Create Each Child List Item */}
                            {pibling.childNodes.map((value: Node) => {
                                if (childNodeFilter(value)) {
                                    const labelId = `checkbox-list-label-${value.id}`;
                                    return (
                                        <ListItem
                                            key={value.id}
                                            role={undefined}
                                            dense
                                            button
                                            onClick={() => send({ type: "SELECT_OR_DESELECT_NODE", payload: { id: value.id, parentId: value.parentId, name: value.name } })}
                                            disabled={!canRead(value.authorisation)}
                                        >
                                            <ListItemText id={labelId} primary={value.name} />

                                            <Checkbox
                                                edge="start"
                                                color="primary"
                                                checked={isSelected(value.id)}
                                                tabIndex={-1}
                                                disableRipple
                                                inputProps={{ "aria-labelledby": labelId }}
                                            />
                                        </ListItem>
                                    );
                                }
                                return null;
                            })}
                        </List>
                    </Collapse>
                </StyledList>
            ))}
        </>
    );
};

const StyledList = styled(List)`
    .parent-item {
        border-top: 1px solid rgba(0, 0, 0, 0.12);

        .caret {
            color: #757575;
            margin-right: 16px;
        }
        .caret.expanded {
            margin-right: 12px;
        }
    }

    .final-parent:not(.expanded) {
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    }

    .child-item {
        padding-left: 28px;

        .MuiListItem-gutters {
            padding-left: 12px;
        }
    }
    button:disabled {
        background-color: rgba(0, 0, 0, 0.2);
    }
`;
